import { Link } from 'react-router-dom';
import CtaBand from '../components/CtaBand';
import GuideCard from '../components/GuideCard';
import Icon from '../components/Icon';
import Layout, { PageHero } from '../components/Layout';
import { AFTER_UPDATE_CHECKLIST, UPDATE_AREAS } from '../constants/content';
import { guidesIn } from '../constants/guides';

const SIGNS = [
  { title: 'Buttons moved or renamed', text: 'Usually a design change. Look in nearby menus or the profile’s “more” options before assuming a feature is gone.' },
  { title: 'Pages look broken', text: 'Your browser may be holding an old copy of the site. Refresh with Ctrl + F5, or clear the site’s cache.' },
  { title: 'App won’t open', text: 'The app updated but the phone hasn’t. Restart the device and install any waiting system update.' },
  { title: 'Numbers changed in DNA', text: 'An estimate refresh has likely rolled out. Matches and notes are generally unaffected.' },
];

export default function UpdatesPage() {
  return (
    <Layout>
      <PageHero
        eyebrow="Updates hub"
        title="Staying up to date — without surprises"
        lead="How apps, browsers, DNA estimates, and sync tools change over time, and what to check after each update."
        crumbs={[{ label: 'Updates' }]}
      />

      <section className="section">
        <div className="container">
          <div className="section-head">
            <p className="eyebrow">What gets updated</p>
            <h2>Four kinds of update that affect your research</h2>
          </div>
          <div className="card-grid card-grid--2">
            {UPDATE_AREAS.map((u) => (
              <article key={u.title} className="update-card">
                <span className="service-card__icon">
                  <Icon name={u.icon} size={26} />
                </span>
                <h3>{u.title}</h3>
                <p>{u.text}</p>
                <ul className="tick-list">
                  {u.checks.map((c) => (
                    <li key={c}>
                      <Icon name="check" size={18} /> {c}
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section section--soft">
        <div className="container checklist-grid">
          <div>
            <p className="eyebrow">After any update</p>
            <h2>A five-minute post-update checklist</h2>
            <p>
              Run through this list whenever the app or website looks different. It catches most problems before
              they cost you any research time.
            </p>
          </div>
          <ol className="checklist">
            {AFTER_UPDATE_CHECKLIST.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ol>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="section-head">
            <p className="eyebrow">Signs of an update</p>
            <h2>“It was working yesterday…”</h2>
            <p>If something changed overnight, an update is the most likely reason. Here is what each sign usually means.</p>
          </div>
          <div className="card-grid card-grid--4">
            {SIGNS.map((s) => (
              <article key={s.title} className="why-card">
                <Icon name="bell" size={26} />
                <h3>{s.title}</h3>
                <p>{s.text}</p>
              </article>
            ))}
          </div>
          <p className="center muted">
            Still stuck? Use the <Link to="/troubleshooting">troubleshooting finder</Link> for step-by-step fixes.
          </p>
        </div>
      </section>

      <section className="section section--soft">
        <div className="container">
          <div className="section-head">
            <p className="eyebrow">Guides</p>
            <h2>Apps &amp; updates guides</h2>
          </div>
          <div className="card-grid card-grid--3">
            {guidesIn('updates').map((g) => (
              <GuideCard key={g.slug} guide={g} />
            ))}
          </div>
        </div>
      </section>

      <CtaBand title="Did an update break something?" text="Tell us what changed and when. We’ll help you get things back the way you need them." />
    </Layout>
  );
}
